import { Auth, authState } from './firebase/auth';
import { firstValueFrom } from 'rxjs';
import { filter, switchMap, take, timeout } from 'rxjs/operators';

/**
 * Reloads the Firebase user and forces a new ID token so JWT claims (e.g. email_verified)
 * match the server. The REST API uses the Bearer token; a cached pre-verification token
 * keeps email_verified false until refreshed.
 */
export async function refreshFirebaseIdToken(auth: Auth): Promise<void> {
  const u = auth.currentUser;
  if (!u) return;
  await u.reload();
  await u.getIdToken(true);
}

/**
 * After applyActionCode (email verification, etc.), `currentUser` can still be null briefly
 * while IndexedDB persistence restores. Wait for a signed-in session, then refresh the token.
 */
export async function refreshFirebaseSessionAfterEmailAction(
  auth: Auth
): Promise<void> {
  try {
    await firstValueFrom(
      authState(auth).pipe(
        filter((u): u is NonNullable<typeof u> => u != null),
        take(1),
        timeout(10_000),
        switchMap(async u => {
          await u.reload();
          await u.getIdToken(true);
        })
      )
    );
  } catch {
    await refreshFirebaseIdToken(auth);
  }
}
